import React from 'react';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from '@/core/i18n/i18n';

interface NavigationProps {
  onNext: () => void;
  disableNext: boolean;
  onBack?: (() => void) | undefined;
  onSaveDraft?: (() => void) | undefined;
  disableSaveDraft?: boolean | undefined;
  onExit?: (() => void) | undefined;
  exitTitle?: string | undefined;
  nextTitle: string;
  backTitle?: string | undefined;
  saveDraftTitle?: string | undefined;
  isAnyLoading?: boolean | undefined;
  readOnlyMode?: boolean | null;
  disableReason?: string | undefined;
  disableBack?: boolean | undefined;
}

const Navigation: React.FC<NavigationProps> = (props: NavigationProps) => {
  const { t } = useTranslation();
  const [pendingAction, setPendingAction] = React.useState<'exit' | 'back' | null>(null);
  const {
    onNext,
    onSaveDraft,
    onBack,
    onExit,
    nextTitle,
    backTitle,
    saveDraftTitle,
    readOnlyMode,
    exitTitle,
    disableNext,
    disableSaveDraft = false,
    isAnyLoading = false,
    disableReason,
    disableBack = false,
  } = props;

  const showDisableTooltip = disableNext && Boolean(disableReason);
  const tooltipTitle = showDisableTooltip ? disableReason : '';
  const confirmationOpen = Boolean(pendingAction);

  const handleRequestConfirmation = (action: 'exit' | 'back') => {
    if (isAnyLoading) return;
    if (readOnlyMode) {
      if (action === 'exit') {
        onExit?.();
      } else {
        onBack?.();
      }
      return;
    }
    setPendingAction(action);
  };

  const closeConfirmation = () => {
    setPendingAction(null);
  };

  const handleConfirm = () => {
    if (pendingAction === 'exit' && onExit) {
      onExit();
    }
    if (pendingAction === 'back' && onBack) {
      onBack();
    }
    setPendingAction(null);
  };

  const confirmLabel =
    pendingAction === 'exit' ? t('navigation.confirmExit') : t('navigation.confirmBack');

  return (
    <>
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        sx={{
          mt: 4,
          width: '100%',
          flexWrap: 'wrap',
          rowGap: { xs: 1.5, sm: 0 },
          columnGap: { xs: 0, sm: 2 },
        }}
      >
        <Grid
          container
          size={{ xs: 12, sm: 'auto' }}
          alignItems="center"
          sx={{
            width: { xs: '100%', sm: 'auto' },
            flexWrap: 'wrap',
            rowGap: { xs: 1, sm: 0 },
            columnGap: { xs: 0, sm: 1 },
            pr: { xs: 0, sm: 3 },
          }}
        >
          {onExit && (
            <Grid
              size={{ xs: 12, sm: 'auto' }}
              sx={{
                display: 'flex',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <Button
                variant="outlined"
                disableElevation
                onClick={() => handleRequestConfirmation('exit')}
                disabled={isAnyLoading}
                sx={{
                  borderColor: '#00BCE4',
                  color: '#00BCE4',
                  px: 4,
                  py: 1,
                  fontWeight: 600,
                  borderRadius: 20,
                  textTransform: 'none',
                  width: { xs: '100%', sm: 'auto' },
                  '&:hover': {
                    borderColor: '#00acc1',
                    bgcolor: 'rgba(0, 188, 212, 0.04)',
                  },
                }}
              >
                {exitTitle}
              </Button>
            </Grid>
          )}
          {!disableBack && onBack && (
            <Grid
              size={{ xs: 12, sm: 'auto' }}
              sx={{
                display: 'flex',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <Button
                variant="outlined"
                disableElevation
                onClick={() => handleRequestConfirmation('back')}
                disabled={isAnyLoading}
                sx={{
                  borderColor: '#00BCE4',
                  color: '#00BCE4',
                  px: 4,
                  py: 1,
                  fontWeight: 600,
                  borderRadius: 20,
                  textTransform: 'none',
                  width: { xs: '100%', sm: 'auto' },
                  '&:hover': {
                    borderColor: '#00acc1',
                    bgcolor: 'rgba(0, 188, 212, 0.04)',
                  },
                }}
              >
                {backTitle}
              </Button>
            </Grid>
          )}
        </Grid>
        <Grid
          container
          size={{ xs: 12, sm: 'auto' }}
          alignItems="center"
          justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            flexWrap: 'wrap',
            rowGap: { xs: 1.5, sm: 0 },
            columnGap: { xs: 0, sm: 1 },
            mt: { xs: 1, sm: 0 },
          }}
        >
          {onSaveDraft && !readOnlyMode && (
            <Grid
              size={{ xs: 12, sm: 'auto' }}
              sx={{
                display: 'flex',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <Button
                variant="outlined"
                disableElevation
                onClick={onSaveDraft}
                disabled={disableSaveDraft || isAnyLoading}
                sx={{
                  borderColor: '#00BCE4',
                  color: '#00BCE4',
                  px: 4,
                  py: 1,
                  borderRadius: 20,
                  fontWeight: 600,
                  textTransform: 'none',
                  width: { xs: '100%', sm: 'auto' },
                  '&:hover': {
                    borderColor: '#00acc1',
                    bgcolor: 'rgba(0, 188, 212, 0.04)',
                  },
                }}
              >
                {saveDraftTitle}
              </Button>
            </Grid>
          )}

          <Grid
            size={{ xs: 12, sm: 'auto' }}
            sx={{
              display: 'flex',
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <Tooltip
              title={tooltipTitle}
              arrow
              placement="top"
              disableFocusListener={!showDisableTooltip}
              disableHoverListener={!showDisableTooltip}
              disableTouchListener={!showDisableTooltip}
            >
              <span
                style={{
                  width: '100%',
                  display: 'inline-flex',
                }}
              >
                <Button
                  variant="contained"
                  onClick={onNext}
                  disableElevation
                  disabled={disableNext || isAnyLoading}
                  sx={{
                    bgcolor: '#00BCE4',
                    color: 'white',
                    px: 4,
                    py: 1,
                    borderRadius: 20,
                    fontWeight: 600,
                    textTransform: 'none',
                    width: { xs: '100%', sm: 'auto' },
                    '&:hover': { bgcolor: '#00acc1' },
                  }}
                >
                  {nextTitle}
                </Button>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Grid>

      <Dialog
        open={confirmationOpen}
        onClose={closeConfirmation}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 6,
            p: { xs: 1, sm: 0 },
          },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <WarningAmberRounded sx={{ color: '#f57c00' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('navigation.confirmTitle')}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body1" sx={{ color: '#424242', lineHeight: 1.6 }}>
            {t('navigation.confirmDescription')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end', gap: 1.5, pb: 3, pr: 3 }}>
          <Button
            variant="outlined"
            disableElevation
            onClick={closeConfirmation}
            disabled={isAnyLoading}
            sx={{
              borderColor: '#00BCE4',
              color: '#00BCE4',
              px: 3,
              py: 1,
              fontWeight: 600,
              borderRadius: 20,
              textTransform: 'none',
              minWidth: 130,
              '&:hover': {
                borderColor: '#00acc1',
                bgcolor: 'rgba(0, 188, 212, 0.04)',
              },
            }}
          >
            {t('navigation.keepEditing')}
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleConfirm}
            disabled={isAnyLoading}
            sx={{
              bgcolor: '#00BCE4',
              color: 'white',
              px: 3,
              py: 1,
              borderRadius: 20,
              fontWeight: 700,
              textTransform: 'none',
              minWidth: 140,
              '&:hover': { bgcolor: '#00acc1' },
            }}
          >
            {confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Navigation;
